// CaseFilesSelector.tsx - Fixed with proper reset

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import dayjs from 'dayjs'
import {useSelector} from 'react-redux'
import {useSearchParams} from 'react-router-dom'
import {Formik} from 'formik'
import FormikSelectList from 'components/atom/Dropdown/FormikSelectList'
import Page from 'components/page/Page'
import {
  getAllCaseRecord,
  resetCaseRecordState,
} from 'redux/Slices/AppSlice/CaseRecords/CaseRecords.slice'
import {RootState} from 'redux/store'
import useDispatchAction from '@hooks/useDispatchAction'
import {CaseRecordsForm} from 'screens/CaseRecords/components'
import Footer from '../components/Footer'
import {nextStep} from 'redux/Slices/AppSlice/orders/orders.slice'
import When from 'components/when/When'
import userOrderDetails from '../hooks/userOrderDetails'
import {safeParseInt} from 'utils/ConstFunctions'
import {AuthContext} from 'context/AuthContext'
import {UploadFile, message} from 'antd'
import {APIPostData, CaseRecordResponse} from 'redux/Slices/AppSlice/CaseRecords/CaseRecord.type'
import {useCreateCaseRecordAction} from 'screens/CaseRecords/hook'
import {useStepper} from 'contexts/StepperContext'
import useCreateOrder from '@hooks/useCreateOrder'
import { hasValue } from 'utils/hasValue'

export const CaseFilesSelector: React.FC = () => {
  const {dispatchAction} = useDispatchAction()
  const {createNewCaseRecord} = useCreateCaseRecordAction()
  const {handleCreateOrder} = useCreateOrder()
  const {selectedCaseRecordId, setSelectedCaseRecordId, setShouldMapCaseRecord} = useStepper()
  const [hideAddButton, setHideButton] = useState<boolean>(false)
  const AddCaseRecordButtonText = 'Add Case Files'
  const [isUploading, setIsUploading] = useState(false)
  const [isSavingCaseRecord, setIsSavingCaseRecord] = useState(false)
  const [isFetchingCaseRecords, setIsFetchingCaseRecords] = useState(false)
  const isSubmittingRef = useRef(false)
  const {profileId, userId} = useContext(AuthContext)
  const [searchParams] = useSearchParams()
  const isRefinementFlow =
    searchParams.get('refinement') === 'true' || searchParams.get('refinement-draft') === 'true'
  const {loadingGetAll} = useSelector((state: RootState) => state.caseRecord)
  
  const [combinedCaseRecords, setCombinedCaseRecords] = useState<CaseRecordResponse[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)
  // Force re-render key
  const [formKey, setFormKey] = useState<number>(Date.now())
  // Additional reset key for CaseRecordsForm - increment this to force reset
  const [resetKey, setResetKey] = useState<number>(Date.now())
  
  const caseRecords = useMemo(() => combinedCaseRecords ?? [], [combinedCaseRecords])
  const hasLoadedCaseRecords =
    !isFetchingCaseRecords && !loadingGetAll && Array.isArray(combinedCaseRecords)
  const noCaseRecords = hasLoadedCaseRecords && caseRecords.length === 0
  const {order} = userOrderDetails()
  const caseRecordIdFromOrder = safeParseInt(order?.case_record_id) || null
  const orderId = order?.order_id
  const patientId = order?.patient_details?.id
  const submitRef = useRef<(() => Promise<boolean>) | null>(null)
  const attachSubmit = (fn: () => Promise<boolean>) => {
    submitRef.current = fn
  }
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>([])
  const [uploadedScanFiles, setUploadedScanFiles] = useState<UploadFile[]>([])
  const [uploadedXrayFiles, setUploadedXrayFiles] = useState<UploadFile[]>([])
  const addCaseRecordOptionValue = '__ADD_CASE_FILES__'

  // Reset everything when order changes
  useEffect(() => {
    setFormKey(Date.now())
    setResetKey(Date.now())
    setSelectedCaseRecordId(null)
    setUploadedFiles([])
    setUploadedScanFiles([])
    setUploadedXrayFiles([])
    setHideButton(false)
    dispatchAction(resetCaseRecordState())
  }, [orderId, setSelectedCaseRecordId, dispatchAction])

  const initialDropdownCaseRecordId = useMemo(() => {
    if (isFetchingCaseRecords || loadingGetAll) return null
    if (selectedCaseRecordId) return selectedCaseRecordId
    if (!caseRecordIdFromOrder) return null
    const hasOrderCaseRecord = caseRecords.some(
      (record) => safeParseInt(record.case_record_id) === caseRecordIdFromOrder
    )
    return hasOrderCaseRecord ? caseRecordIdFromOrder : null
  }, [caseRecordIdFromOrder, caseRecords, selectedCaseRecordId, isFetchingCaseRecords, loadingGetAll])

  const getNextButtonText = () => {
    if (isUploading) return 'Uploading files...'
    if (isSavingCaseRecord) return 'Saving case files...'
    return 'Save & continue'
  }

  const extractNewFileIds = (files: UploadFile[]) =>
    files
      .filter((file) => typeof file.uid !== 'string')
      .map((file) => {
        if (typeof file.uid === 'number') return file.uid
        return safeParseInt(file.uid)
      })
      .filter((id) => !!id && !Number.isNaN(id))

  const handleSubmitCaseRecord = async () => {
    if (isUploading || isSavingCaseRecord || isSubmittingRef.current) return
    if (!submitRef.current) return
    isSubmittingRef.current = true
    setIsSavingCaseRecord(true)
    try {
      await submitRef.current()
    } finally {
      setIsSavingCaseRecord(false)
      isSubmittingRef.current = false
    }
  }

  const getCaseRecordById = (caseRecordId?: number | null): CaseRecordResponse | undefined =>
    caseRecords.find((record) => safeParseInt(record.case_record_id) === safeParseInt(caseRecordId))

  const saveSelectedCaseRecordAndContinue = async (selectedCaseRecordId?: number | null) => {
    if (
      !selectedCaseRecordId ||
      !order?.order_id ||
      !order?.status ||
      !order?.patient_details?.id ||
      !order?.doctor_id
    ) {
      console.error('Missing required data for saving case record', {
        selectedCaseRecordId,
        orderId: order?.order_id,
        patientId: order?.patient_details?.id,
        doctorId: order?.doctor_id,
      })
      return
    }

    try {
      const selectedCaseRecord = getCaseRecordById(selectedCaseRecordId)

      const requestBody: APIPostData = {
        chief_complaint: selectedCaseRecord?.chief_complaint ?? '',
        pre_treatment_file_ids: extractNewFileIds(uploadedFiles),
        scan_file_ids: extractNewFileIds(uploadedScanFiles),
        xray_file_ids: extractNewFileIds(uploadedXrayFiles),
        patient_id: safeParseInt(patientId),
        profile_id: safeParseInt(profileId),
        doctor_id: safeParseInt(userId),
        case_record_id: selectedCaseRecordId ?? null,
        order_id: order.order_id,
      }

      const caseRecordResponse = await createNewCaseRecord(requestBody)

      const resolvedCaseRecordId =
        safeParseInt(caseRecordResponse?.case_record_id) ||
        safeParseInt(caseRecordResponse?.case_record?.case_record_id) ||
        safeParseInt(caseRecordResponse?.case_record_details?.case_record_id) ||
        selectedCaseRecordId

      setSelectedCaseRecordId(resolvedCaseRecordId)
      setShouldMapCaseRecord(true)

      await handleCreateOrder({
        orderPayload: {
          map_to_case_record: true,
          case_record_id: resolvedCaseRecordId,
          order_id: order.order_id,
          status: order.status,
          patient_id: order.patient_details.id,
          doctor_id: safeParseInt(userId),
          profile_id: safeParseInt(profileId),
          practice_doctor_id: safeParseInt(order?.doctor_id),
          practice_profile_id: safeParseInt(order?.profile_id),
          practice_organization_id: safeParseInt(order?.organization_id),
        },
        product: order?.service_products ?? null,
        assignedCustomer: order?.patient_details ?? null,
      })

      dispatchAction(nextStep())
    } catch (error) {
      console.error('Failed to save case record', error)
      message.error('Unable to save case files. Please try again.')
    }
  }

  const submitSelectedCaseRecord = async (selectedCaseRecordId?: number | null) => {
    if (isUploading || isSavingCaseRecord || isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSavingCaseRecord(true)
    try {
      await saveSelectedCaseRecordAndContinue(selectedCaseRecordId)
    } finally {
      setIsSavingCaseRecord(false)
      isSubmittingRef.current = false
    }
  }

  // Force reset function for the form
  const forceResetForm = () => {
    setResetKey(Date.now())
    setUploadedFiles([])
    setUploadedScanFiles([])
    setUploadedXrayFiles([])
    dispatchAction(resetCaseRecordState())
  }

  useEffect(() => {
    let isCurrentRequest = true

    const fetchCaseRecords = async () => {
      // Reset all state
      setCombinedCaseRecords([])
      setHideButton(false)
      setSelectedCaseRecordId(null)
      setFetchError(null)
      setIsFetchingCaseRecords(true)
      
      // Force reset the form
      forceResetForm()

      try {
        // Check if patientId exists
        if (!patientId) {
          setIsFetchingCaseRecords(false)
          return
        }

        // Store case records from both API calls
        let orderedCaseRecords: CaseRecordResponse[] = []
        let unmappedCaseRecords: CaseRecordResponse[] = []

        // First API call: Get case records with orderId
        if (orderId) {
          try {
            const response = await dispatchAction(
              getAllCaseRecord({patient_id: patientId, orderId: orderId})
            ).unwrap()
            
            if (isCurrentRequest && Array.isArray(response)) {
              orderedCaseRecords = response
            }
          } catch (error) {
            console.error('Failed to fetch ordered case records:', error)
          }
        }

        // Second API call: Get all case records without orderId
        if (!isRefinementFlow) {
          try {
            const response = await dispatchAction(
              getAllCaseRecord({patient_id: patientId})
            ).unwrap()
            
            if (isCurrentRequest && Array.isArray(response)) {
              
              // Filter out records that are already mapped with an order
              unmappedCaseRecords = response.filter((record) => {
                const isMapped = 
                  record.is_mapped_with_order === true || 
                  record.is_mapped_with_order === 'true' ||
                  record.is_mapped_with_order === 1 ||
                  record.is_mapped_with_order === '1' ||
                  record.is_mapped_with_order === 'TRUE' ||
                  record.is_mapped_with_order === 'True'
                
                
                
                return !isMapped
              })
              
            }
          } catch (error) {
            console.error('Failed to fetch all case records:', error)
          }
        }

        if (isCurrentRequest) {
          const orderedRecordIds = new Set(
            orderedCaseRecords.map(record => safeParseInt(record.case_record_id))
          )

          const uniqueUnmappedRecords = unmappedCaseRecords.filter(
            record => !orderedRecordIds.has(safeParseInt(record.case_record_id))
          )

          const combined = [...orderedCaseRecords, ...uniqueUnmappedRecords]
          
          setCombinedCaseRecords(combined)
        }
      } catch (error) {
        console.error('Failed to fetch case records:', error)
        setFetchError('Failed to load case records. Please try again.')
      } finally {
        if (isCurrentRequest) {
          setIsFetchingCaseRecords(false)
        }
      }
    }

    fetchCaseRecords()

    return () => {
      isCurrentRequest = false
    }
  }, [dispatchAction, patientId, orderId, isRefinementFlow, setSelectedCaseRecordId])

  const shouldShowAddForm = hideAddButton || (hasLoadedCaseRecords && noCaseRecords)
  const shouldShowDropdown = hasLoadedCaseRecords && caseRecords.length > 0 && !hideAddButton

  const caseRecordOptions = caseRecords.map((record: any) => {
    const isMapped = 
      record.is_mapped_with_order === true || 
      record.is_mapped_with_order === 'true' ||
      record.is_mapped_with_order === 1 ||
      record.is_mapped_with_order === '1' ||
      record.is_mapped_with_order === 'TRUE' ||
      record.is_mapped_with_order === 'True'
    
    return {
      value: record.case_record_id,
      label: `Case Record #${record.case_record_id}${
        record?.created_at && dayjs(record.created_at).isValid()
          ? ` (${dayjs(record.created_at).format('DD-MMM-YYYY, hh:mm A')})`
          : ''
      }${isMapped ? '' : ''}`,
    }
  })

  const caseRecordDropdownOptions = [
    {value: addCaseRecordOptionValue, label: AddCaseRecordButtonText},
    ...caseRecordOptions,
  ]

  // Show loading state
  if (isFetchingCaseRecords || loadingGetAll) {
    return (
      <Page loading={true} containerClassName='min-h-full'>
        <div className='flex justify-center items-center py-8'>
          <span>Loading case records...</span>
        </div>
      </Page>
    )
  }

  // Show error state
  if (fetchError) {
    return (
      <Page loading={false} containerClassName='min-h-full'>
        <div className='flex flex-col justify-center items-center py-8'>
          <div className='text-red-600 mb-4'>{fetchError}</div>
          <button 
            className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
            onClick={() => {
              setFetchError(null)
              setFormKey(Date.now())
              forceResetForm()
            }}
          >
            Retry
          </button>
        </div>
      </Page>
    )
  }

  return (
    <Page loading={false} containerClassName='min-h-full'>
      {shouldShowDropdown && (
        <Formik
          enableReinitialize
          key={`formik-${formKey}`}
          initialValues={{case_record: initialDropdownCaseRecordId}}
          onSubmit={async (values) => {
            await submitSelectedCaseRecord(safeParseInt(values.case_record) || null)
          }}
        >
          {({values, handleSubmit, setFieldValue}) => {
            const selectedCaseRecordId = safeParseInt(values.case_record)
            const hasSelectedCaseRecord = selectedCaseRecordId > 0

            useEffect(() => {
              if (!isFetchingCaseRecords && caseRecords.length === 0) {
                setFieldValue('case_record', null)
              }
            }, [caseRecords.length, isFetchingCaseRecords, setFieldValue])

            return (
              <>
                <FormikSelectList
                  name='case_record'
                  items={caseRecordDropdownOptions}
                  label='Select Case Record'
                  placeholder='Select a case record'
                  allowClear
                  onChangeMapperFunc={(value) => value}
                  onChangeSuccess={(selected) => {
                    if (selected?.value === addCaseRecordOptionValue) {
                      dispatchAction(resetCaseRecordState())
                      setHideButton(true)
                      forceResetForm()
                    }
                  }}
                />

                {hasSelectedCaseRecord && (
                  <div className='mt-6'>
                    <CaseRecordsForm
                      key={`case-record-${selectedCaseRecordId}-${orderId}-${resetKey}`}
                      patientId={patientId}
                      isEditMode={true}
                      hideSectionHeader
                      hideChiefComplaint
                      caseRecordId={selectedCaseRecordId}
                      orderId={orderId}
                      onUploadingChange={setIsUploading}
                      enforceScanFileViewCondition
                      setExternalExistingPreTreatmentFileIds={setUploadedFiles}
                      setExternalExistingXrayFileIds={setUploadedXrayFiles}
                      setExternalExistingScanFileIds={setUploadedScanFiles}
                      resetKey={resetKey}
                    />
                  </div>
                )}
                <Footer
                  onNext={() => {
                    if (isUploading || isSavingCaseRecord || isSubmittingRef.current) return
                    handleSubmit()
                  }}
                  nextButtonText={getNextButtonText()}
                  disableNext={isUploading || isSavingCaseRecord || !hasSelectedCaseRecord}
                  loadingNext={isUploading || isSavingCaseRecord}
                />
              </>
            )
          }}
        </Formik>
      )}

      {shouldShowAddForm && (
        <Formik 
          key={`formik-add-${formKey}`}
          initialValues={{case_record: null}} 
          onSubmit={() => {}}
        >
          {({}) => (
            <>
              <CaseRecordsForm
                key={`case-record-form-${orderId}-${resetKey}`}
                patientId={patientId}
                isEditMode={false}
                hideSectionHeader
                hideChiefComplaint
                orderId={orderId}
                onSubmitRef={attachSubmit}
                onUploadingChange={setIsUploading}
                resetKey={resetKey}
              />
              <Footer
                onNext={() => {
                  void handleSubmitCaseRecord()
                }}
                nextButtonText={getNextButtonText()}
                disableNext={isUploading || isSavingCaseRecord}
                loadingNext={isUploading || isSavingCaseRecord}
              />
            </>
          )}
        </Formik>
      )}

      {/* Show message when no case records and not showing add form */}
      {!shouldShowDropdown && !shouldShowAddForm && (
        <div className='flex flex-col items-center justify-center py-8'>
          <p className='text-gray-600'>No case records available.</p>
          <button
            className='mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
            onClick={() => {
              setHideButton(true)
              forceResetForm()
            }}
          >
            Add Case Files
          </button>
        </div>
      )}
    </Page>
  )
}